import {
  applyMention,
  mentionCandidates,
  mentionQueryAt,
  mentionSegments,
  parseMentions,
  type Job,
  type Mentionable,
} from "@roof-hawk/core";
import { useEffect, useRef, useState } from "react";

import { Stub } from "../../components/ui";
import { t } from "../../i18n";
import { fmtDateTime } from "../../lib/format";
import { useAsync } from "../../lib/hooks";
import { emailNoteMentions } from "../../lib/noteEmail";
import {
  fetchNotes,
  insertRow,
  signNotePhotos,
  uploadNotePhoto,
  type NotePhoto,
  type NoteRow,
} from "../../lib/queries";
import { useSession } from "../../session";
import { useGmailAccount } from "../email/useGmailAccount";

/**
 * Notes are scoped to the job and rolled up on the customer (ADR 0005).
 *
 * A note is now a conversation rather than a line in a log. Type `@` and a
 * colleague's name to tag them, and answer any note underneath it — Jonathon,
 * 2026-08-25: "I want to be able to tag other reps in notes and be able to
 * reply inside of roofboard to each other."
 *
 * Everybody tagged, and everybody already in a thread that has been answered,
 * gets an email with the note in it and a link back here. That is the point of
 * the feature: the answer lives in Roof Board, but nobody is expected to be
 * looking at Roof Board when the question is asked.
 *
 * A note can also carry photographs. Jonathon, 2026-09-01: "we want to be able
 * to attach photos to our notes." Rotten decking under a tear-off, the
 * neighbour's fence the crew has to work around, the shingle that came in the
 * wrong colour — half of what gets said about a roof is only sayable with a
 * picture, and until now that picture went to somebody's personal phone and the
 * job never had it. They are not proposal photos and do not go near one; see
 * the migration for why that is two tables and not one.
 *
 * Calls and texts logged against the job still show here, and are not
 * replied to — there is nothing on the other end of a text log to answer.
 */
export function NotesPanel({ job }: { job: Job }) {
  const { profileId, people } = useSession();
  const { account } = useGmailAccount();
  const notes = useAsync(() => fetchNotes(job.id), [job.id]);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const rows = notes.data ?? [];

  /*
   * The links to the photographs, minted for the whole panel at once.
   *
   * The bucket is private, so nothing is readable without one. One request for
   * every photograph on the job rather than one each: a busy job's notes would
   * otherwise open a round trip per thumbnail every time the tab is opened.
   * They last an hour, which is longer than anybody keeps this screen open.
   */
  const paths = rows.flatMap((row) => (row.photos ?? []).map((p) => p.path));
  const links = useAsync(
    () => signNotePhotos(paths),
    [job.id, paths.join(",")],
  );
  /** The notes that start a thread, newest first — the order they arrive in. */
  const threads = rows.filter((row) => !row.parent_id);
  /** A thread's answers, oldest first, which is the order it was said in. */
  const repliesTo = (id: string) =>
    rows
      .filter((row) => row.parent_id === id)
      .slice()
      .sort(
        (a, b) =>
          new Date(a.occurred_at).getTime() - new Date(b.occurred_at).getTime(),
      );

  // English, whatever this phone reads in: the name goes into the tag email,
  // which leaves the app (ADR 0325).
  const nameOf = (id: string | null) =>
    people.find((person) => person.id === id)?.full_name ?? "Somebody";

  /**
   * Write the note, then tell whoever needs telling.
   *
   * In that order, and the email failing does not undo the note. A tag that is
   * recorded but not emailed is a tag somebody sees the next time they open the
   * job; a note that was rolled back because an inbox was full is gone.
   */
  async function post(body: string, files: File[], parent: NoteRow | null) {
    const mentions = parseMentions(body, people);
    const written = await insertRow<{ id: string }>("activities", {
      job_id: job.id,
      contact_id: job.contact_id,
      type: "note",
      body,
      parent_id: parent?.id ?? null,
      mentions,
      created_by: profileId,
    });

    /*
     * The photographs after the note, in the order they were picked, and a
     * failure here does not undo the note either.
     *
     * One at a time rather than all at once: a rep on a driveway is uploading
     * over one bar of signal, and four parallel uploads on that connection is
     * how all four time out instead of the first three arriving. What is said
     * about a failure names the file, because the person still has it and can
     * try again.
     */
    const lost: string[] = [];
    for (const [at, file] of files.entries()) {
      try {
        await uploadNotePhoto(written.id, job.id, file, at, profileId);
      } catch {
        lost.push(file.name);
      }
    }

    setReplyingTo(null);
    await notes.reload();

    // On a reply, everybody already in the conversation hears about it, not
    // only the people named in this particular sentence.
    const thread = parent
      ? [
          parent.created_by ?? "",
          ...(parent.mentions ?? []),
          ...repliesTo(parent.id).flatMap((reply) => [
            reply.created_by ?? "",
            ...(reply.mentions ?? []),
          ]),
        ]
      : [];

    // Said first and on its own, because it is the only part of this the
    // person has to do something about.
    const photoTrouble = lost.length
      ? lost.length === 1
        ? t("Could not attach {files} — the note saved without it.", {
            files: lost.join(", "),
          })
        : t("Could not attach {files} — the note saved without them.", {
            files: lost.join(", "),
          })
      : null;

    if (mentions.length === 0 && thread.length === 0) {
      setStatus(photoTrouble);
      return;
    }

    if (!account) {
      setStatus(
        [
          photoTrouble,
          t(
            "Saved. Nobody was emailed — connect your Google account on the Email tab and tags will go out from it.",
          ),
        ]
          .filter(Boolean)
          .join(" "),
      );
      return;
    }

    const { sent, failed } = await emailNoteMentions({
      job,
      body,
      mentions,
      thread,
      author: profileId,
      authorName: nameOf(profileId),
      people: people as Mentionable[],
      isReply: Boolean(parent),
    });

    setStatus(
      [
        photoTrouble,
        sent.length ? t("Emailed {names}.", { names: sent.join(", ") }) : null,
        failed.length
          ? t("Could not email {names}.", { names: failed.join(", ") })
          : null,
      ]
        .filter(Boolean)
        .join(" ") || null,
    );
  }

  return (
    <div className="block">
      <h2>{t("Notes — Calls & Texts")}</h2>

      <NoteComposer
        people={people}
        placeholder={t("Add a note… type @ to tag someone")}
        submitLabel={t("Add")}
        onSubmit={(body, files) => post(body, files, null)}
      />

      {status && (
        <div className="na-hint" role="status" style={{ marginTop: 6 }}>
          {status}
        </div>
      )}

      {threads.length === 0 ? (
        <Stub
          title={t("No notes on this job yet")}
          message={t(
            "Notes here stay with this job. The customer page shows every job's notes together. Tag a colleague with @ and they get an email.",
          )}
        />
      ) : (
        threads.map((note) => (
          <div className="notethread" key={note.id}>
            <NoteBody note={note} people={people} links={links.data ?? {}} />

            {repliesTo(note.id).map((reply) => (
              <div className="notereply" key={reply.id}>
                <NoteBody
                  note={reply}
                  people={people}
                  links={links.data ?? {}}
                />
              </div>
            ))}

            {/* Only a note somebody wrote can be answered. A logged call or
                text has nobody on the other end of it. */}
            {(note.type ?? "note") === "note" &&
              (replyingTo === note.id ? (
                <div className="notereply">
                  <NoteComposer
                    people={people}
                    autoFocus
                    placeholder={t("Reply… type @ to tag someone")}
                    submitLabel={t("Reply")}
                    onCancel={() => setReplyingTo(null)}
                    onSubmit={(body, files) => post(body, files, note)}
                  />
                </div>
              ) : (
                <button
                  className="btn-small notereplybtn"
                  onClick={() => setReplyingTo(note.id)}
                >
                  {t("Reply")}
                </button>
              ))}
          </div>
        ))
      )}
    </div>
  );
}

/** One note, with the names in it drawn as tags rather than as text. */
function NoteBody({
  note,
  people,
  links,
}: {
  note: NoteRow;
  people: readonly Mentionable[];
  /** Signed URL by object path, minted for the panel. */
  links: Record<string, string>;
}) {
  const photos = (note.photos ?? [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);
  return (
    <div className="note">
      <div className={`ntype ${note.type}`}>
        {t((note.type ?? "note").toUpperCase())}
      </div>
      <div style={{ flex: 1 }}>
        <div className="nmeta">
          {note.direction === "inbound" && (
            <span className="ndir in">{t("Inbound")}</span>
          )}
          {note.direction === "outbound" && (
            <span className="ndir out">{t("Outbound")}</span>
          )}
          <span>{note.author?.full_name ?? ""}</span>
          <span>·</span>
          <span>{fmtDateTime(note.occurred_at)}</span>
        </div>
        <div>
          {mentionSegments(note.body ?? "", people).map((segment, at) =>
            segment.profileId ? (
              <span className="mention" key={at}>
                {segment.text}
              </span>
            ) : (
              <span key={at}>{segment.text}</span>
            ),
          )}
        </div>
        {photos.length > 0 && (
          <div className="notephotos">
            {photos.map((photo) => (
              <NotePhotoThumb
                key={photo.id}
                photo={photo}
                href={links[photo.path]}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * One photograph on a note.
 *
 * A thumbnail that opens the full picture in its own tab, which is what a rep
 * wants on a phone: the point of the photo is usually a detail, and a 90px
 * square of a roof is not a detail.
 *
 * A missing link is drawn as a named placeholder rather than as a broken
 * image. It means the signing round trip has not landed yet, or that the
 * object is gone — neither is worth showing somebody a torn page icon over.
 */
function NotePhotoThumb({
  photo,
  href,
}: {
  photo: NotePhoto;
  href: string | undefined;
}) {
  const name = photo.filename ?? t("Photo");
  if (!href) return <div className="notephoto na-hint">{name}</div>;
  return (
    <a
      className="notephoto"
      href={href}
      target="_blank"
      rel="noreferrer"
      title={name}
    >
      <img src={href} alt={name} loading="lazy" />
    </a>
  );
}

/**
 * The box a note is typed into, with the `@` picker attached.
 *
 * The picker opens on `@` and closes on Escape, on a space too many, or on
 * picking somebody. Arrow keys move through it and Enter takes the highlighted
 * name — the same keys as everywhere else that has a list under a box, because
 * a rep should not have to learn this one.
 */
function NoteComposer({
  people,
  placeholder,
  submitLabel,
  autoFocus,
  onSubmit,
  onCancel,
}: {
  people: readonly Mentionable[];
  placeholder: string;
  submitLabel: string;
  autoFocus?: boolean;
  onSubmit: (body: string, files: File[]) => Promise<void>;
  onCancel?: () => void;
}) {
  const [draft, setDraft] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState<{
    query: string;
    from: number;
  } | null>(null);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLTextAreaElement>(null);
  const picker = useRef<HTMLInputElement>(null);
  /*
   * Both of the delayed things this box does — closing the list after a blur,
   * and putting the caret back after a pick — outlive the component if the
   * panel is torn down in between. Neither is worth a state update on a
   * component that has gone.
   */
  const closing = useRef<number | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (closing.current !== null) window.clearTimeout(closing.current);
    };
  }, []);

  const hits = picking ? mentionCandidates(people, picking.query) : [];

  const reread = (value: string, caret: number) => {
    const at = mentionQueryAt(value, caret);
    // Only while it still matches somebody. A note that happens to contain an
    // "@" should not leave a list hanging open over the rest of the screen.
    setPicking(at && mentionCandidates(people, at.query).length ? at : null);
    setActive(0);
  };

  const choose = (person: Mentionable) => {
    const caret = box.current?.selectionStart ?? draft.length;
    if (!picking) return;
    const next = applyMention(draft, picking.from, caret, person);
    setDraft(next.text);
    setPicking(null);
    // After React has painted the new value, or the caret lands where the old
    // one ended.
    requestAnimationFrame(() => {
      if (!alive.current) return;
      box.current?.focus();
      box.current?.setSelectionRange(next.caret, next.caret);
    });
  };

  /**
   * The photographs waiting to go with this note.
   *
   * Added rather than replaced. A phone's picker comes back one photo at a
   * time as often as not — some Android galleries have no multi-select at all
   * — and a rep who picks the second one should not find the first has gone.
   * The input is cleared after each pick so that choosing the same file twice
   * in a row still fires a change event.
   */
  function attach(picked: FileList | null) {
    if (!picked?.length) return;
    setFiles((held) => [...held, ...Array.from(picked)]);
  }

  async function send() {
    const body = draft.trim();
    // A photograph on its own is a note. "Look at this" with a picture of the
    // decking under it says more than most sentences typed into this box.
    if ((!body && files.length === 0) || busy) return;
    setBusy(true);
    try {
      await onSubmit(body, files);
      setDraft("");
      setFiles([]);
      setPicking(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="addnote">
      <div className="notebox">
        <textarea
          ref={box}
          rows={2}
          autoFocus={autoFocus}
          placeholder={placeholder}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            reread(e.target.value, e.target.selectionStart ?? 0);
          }}
          onClick={(e) =>
            reread(draft, (e.target as HTMLTextAreaElement).selectionStart ?? 0)
          }
          onBlur={() => {
            // Late enough for a click on the list to land first.
            if (closing.current !== null) window.clearTimeout(closing.current);
            closing.current = window.setTimeout(() => {
              if (alive.current) setPicking(null);
            }, 150);
          }}
          onKeyDown={(e) => {
            if (picking && hits.length) {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((at) => (at + 1) % hits.length);
                return;
              }
              if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((at) => (at - 1 + hits.length) % hits.length);
                return;
              }
              if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                choose(hits[active] ?? hits[0]!);
                return;
              }
              if (e.key === "Escape") {
                e.preventDefault();
                setPicking(null);
                return;
              }
            }
            // Enter posts; Shift+Enter is a new line, because a note about a
            // roof is sometimes two sentences.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
            if (e.key === "Escape" && onCancel) onCancel();
          }}
        />
        {files.length > 0 && (
          <div className="notepicked">
            {files.map((file, at) => (
              <span className="notepickedone" key={`${file.name}-${at}`}>
                {file.name}
                <button
                  type="button"
                  aria-label={t("Remove {name}", { name: file.name })}
                  onClick={() =>
                    setFiles((held) => held.filter((_, index) => index !== at))
                  }
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
        {picking && hits.length > 0 && (
          <div className="mentionlist" role="listbox">
            {hits.map((person, at) => (
              <button
                key={person.id}
                type="button"
                role="option"
                aria-selected={at === active}
                className={at === active ? "on" : undefined}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(person)}
              >
                {person.full_name}
                {!person.email?.trim() && (
                  <span className="na-hint"> · {t("no email on file")}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="notebtns">
        {/* A hidden input behind a button, the same as the Docs card: "choose
            a file" is the control a phone has, and a drop zone is not.

            `capture` is deliberately absent. A rep is as likely to be sending
            a photograph they took an hour ago as one they are taking now, and
            naming a capture device takes the gallery away on some phones. */}
        <input
          ref={picker}
          type="file"
          accept="image/*"
          multiple
          style={{ display: "none" }}
          onChange={(e) => {
            attach(e.target.files);
            // Cleared so that picking the same file twice in a row still
            // fires a change event.
            e.target.value = "";
          }}
        />
        <button
          type="button"
          className="btn-ghost notephotobtn"
          onClick={() => picker.current?.click()}
        >
          {t("Add photo")}
        </button>
        <button
          onClick={() => void send()}
          disabled={busy || (!draft.trim() && files.length === 0)}
        >
          {busy ? t("Saving…") : submitLabel}
        </button>
        {onCancel && (
          <button className="btn-ghost" onClick={onCancel}>
            {t("Cancel")}
          </button>
        )}
      </div>
    </div>
  );
}
